// library/labels — the Library's localized words (msgids live here): kinds,
// tabs, columns, what a row says it is, dates, hints, row actions. A hook
// because Lingui's `t` is; every function is a pure map from data to text.

import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { COUNT_CAP, formatCount } from "../../app/displayLimits.js";
import type { ExportFormatKind } from "../../components/ExportView/ExportView.js";
import type {
    QueryHint,
    QueryScope,
} from "../../components/Search/libraryQuery.js";
import type { LibraryRowAction } from "../../components/Search/libraryRowIntent.js";
import type {
    Recency,
    SearchResult,
    SearchResultKind,
    SortColumn,
} from "../../components/Search/searchModel.js";

// A package's target, short enough for a table cell.
const formatWord = (f: ExportFormatKind, text: string): string | undefined => {
    switch (f.type) {
        case "torus":
            return "Torus";
        case "canvas":
            return "Canvas";
        case "markdown":
            return "Markdown";
        case "html":
            return "HTML";
        case "text":
            return text;
        case "other":
            return f.id;
        case "unspecified":
            return undefined;
    }
};

export const useLibraryLabels = () => {
    const { t, i18n } = useLingui();

    const kind = (k: SearchResultKind): string => {
        switch (k) {
            case "source":
                return t`Source`;
            case "material":
                return t`Material`;
            case "package":
                return t({ message: "Package", context: "resource kind" });
            case "file":
                return t`File`;
            case "conversation":
                return t`Conversation`;
        }
    };

    const scope = (s: QueryScope): string => {
        switch (s) {
            case "all":
                return t`All`;
            case "source":
                return t`Sources`;
            case "material":
                return t`Materials`;
            case "package":
                return t`Packages`;
            case "file":
                return t`Files`;
            case "archived":
                return t`Archived`;
            case "conversation":
                return t`Conversations`;
        }
    };

    const column = (c: SortColumn): string => {
        switch (c) {
            case "title":
                return t({ message: "Title", context: "library column" });
            case "kind":
                return t({ message: "Type", context: "library column" });
            case "author":
                return t({ message: "Author", context: "library column" });
            case "based-on":
                return t({ message: "Based on", context: "library column" });
            case "used-in":
                return t({ message: "Used in", context: "library column" });
            case "edited":
                return t({ message: "Edited", context: "library column" });
        }
    };

    // "Archived", agreeing with the kind's gender in Swedish.
    const archived = (k: SearchResultKind): string => {
        switch (k) {
            case "source":
            case "file":
            case "conversation":
                return t({ message: "Archived", context: "common gender" });
            case "material":
            case "package":
                return t({ message: "Archived", context: "neuter" });
        }
    };

    // The Type cell: the kind, then what kind of it ("Source · PDF",
    // "Quiz · 3 versions", "Package · Torus"), archived first.
    const type = (r: SearchResult): string => {
        const facts = r.facts;
        const detail: readonly string[] =
            facts === undefined
                ? [r.meta]
                : facts.type === "material"
                  ? [
                        facts.template === "quiz"
                            ? t`Quiz`
                            : facts.template === "question"
                              ? t`Question`
                              : "",
                        facts.versions === undefined || facts.versions < 2
                            ? ""
                            : t`${plural(facts.versions, {
                                  one: "# version",
                                  other: "# versions",
                              })}`,
                    ]
                  : [formatWord(facts.format, t`Text`) ?? r.meta];
        return [
            r.archived === true ? archived(r.kind) : "",
            // The Library lists materials (C-01): a material row needn't say
            // so; any other kind does.
            r.kind === "material" ? "" : kind(r.kind),
            ...detail,
        ]
            .filter((s) => s !== "")
            .join(" · ");
    };

    // The Based on cell: a material's sources, a package's material.
    const basedOn = (r: SearchResult): string => {
        const facts = r.facts;
        if (facts === undefined) return "";
        if (facts.type === "package") return facts.from ?? "";
        const [first, ...others] = facts.basedOn;
        const more = others.length;
        return first === undefined
            ? ""
            : more === 0
              ? first
              : t`${first} +${more}`;
    };

    const usedIn = (count: number | undefined): string =>
        count === undefined || count === 0
            ? ""
            : count > COUNT_CAP
              ? t`${formatCount(count, i18n.locale)} materials`
              : t`${plural(count, { one: "# material", other: "# materials" })}`;

    // The Edited cell: an absolute date, scannable over months and years.
    const date = (iso: string | undefined): string => {
        if (iso === undefined) return "";
        const ms = Date.parse(iso);
        return Number.isNaN(ms)
            ? ""
            : new Intl.DateTimeFormat(i18n.locale, {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
              }).format(ms);
    };

    // The same moment, relative (the Edited cell's tooltip).
    const recency = (r: Recency): string => {
        switch (r.type) {
            case "now":
                return t`Just now`;
            case "minutes":
                return t`${r.n} min ago`;
            case "hours":
                return t`${r.n} h ago`;
            case "yesterday":
                return t`Yesterday`;
            case "days":
                return t`${r.n} days ago`;
            case "date":
            case "unknown":
                return "";
            case "raw":
                return r.text;
        }
    };

    const hint = (h: QueryHint): string => {
        switch (h.type) {
            case "unknown_modifier": {
                const name = h.name;
                return t`"${name}:" is not a filter. Filters: type:, author:, created:, status:`;
            }
            case "missing_value": {
                const key = h.key;
                return t`"${key}:" needs a value right after the colon, e.g. ${key}:something`;
            }
            case "unknown_value": {
                const { key, value } = h;
                return h.key === "type"
                    ? t`"${value}" is not a type. Try type:source, material, package or file`
                    : t`"${value}" is not a status. Try ${key}:archived`;
            }
        }
    };

    const action = (a: LibraryRowAction): string => {
        switch (a) {
            case "rename":
                return t`Rename`;
            case "archive":
                return t`Archive`;
            case "unarchive":
                return t`Unarchive`;
            case "delete":
                return t`Delete…`;
        }
    };

    return {
        kind,
        scope,
        column,
        archived,
        type,
        basedOn,
        usedIn,
        date,
        recency,
        hint,
        action,
    };
};

export type LibraryLabels = ReturnType<typeof useLibraryLabels>;
